<template>
    <div :class="[
        'form',
        'form__selectfilters',
        errors.first(toKebabCase(name)) ? 'form__selectfilters--error' : ''
    ]">
        <label
            v-if="label"
            :for="name | toKebabCase"
            class="form__selectfilters_title"
        >{{label | toCapitalize}}<span class="asterisk" v-if="required">*</span></label>
        <template v-if="$slots.default && hintPositionTop">
            <div class="form__selectfilters_hint form__selectfilters_hint--top">
                <slot></slot>
            </div>
        </template>
        <div class="form__selectfilters_wrapper">
            <div class="form__selectfilters_wrapper_fields">
                <form-select
                    :disabled="disabled"
                    :name="'form-selectfilters-column'"
                    :label="'Column'"
                    :items="items"
                    :value="filterColumn"
                    @input="setFilterColumn"
                ></form-select>
                <template v-if="filterColumn && filterColumn.includes('OrganisationUnit')">
                    <form-select-org-units
                        :label="'Organisation unit'"
                        :name="'form-selectfilters-orgunits'"
                        :workspace-id="loggedInUserWorkspaceId"
                        :value="filterThreshold.value"
                        :multiple="false"
                        :check-parent-if-children-selected="false"
                        @input="setFilterValue"
                    ></form-select-org-units>
                </template>
                <template v-else>
                    <template v-if="filterColumn && !filterAvailableValuesMapped.length">
                        <form-radio-horizontal
                            :prevent-vertical="true"
                            :disabled="disabled"
                            :name="'form-selectfilters-type'"
                            :label="'Filter type'"
                            :items="filterTypeList"
                            v-model="filterType"
                        ></form-radio-horizontal>
                    </template>
                    <template v-if="filterType">
                        <template v-if="filterType === 'equals' || filterType === 'like'">
                            <div class="columns is-variable">
                                <div class="column is-6">
                                    <template v-if="filterAvailableValuesMapped && filterAvailableValuesMapped.length">
                                        <form-select
                                            :label="'Value'"
                                            :name="'form-selectfilters-select'"
                                            :value="filterThreshold.value"
                                            :items="filterAvailableValuesMapped"
                                            :searchable="false"
                                            :is-type="'select'"
                                            @input="setFilterValue"
                                        ></form-select>
                                    </template>
                                    <template v-else-if="filterDataType === 'string'">
                                        <form-text
                                            :disabled="disabled"
                                            :name="'form-selectfilters-string'"
                                            :label="'Value'"
                                            v-model="filterThreshold.value"
                                        ></form-text>
                                    </template>
                                    <template v-else-if="filterDataType === 'number'">
                                        <form-number
                                            :disabled="disabled"
                                            :name="'form-selectfilters-number'"
                                            :label="'Value'"
                                            v-model="filterThreshold.value"
                                        ></form-number>
                                    </template>
                                    <template v-else-if="filterDataType === 'date'">
                                        <form-date
                                            :label="'Date to'"
                                            :name="'form-selectfilters-date'"
                                            v-model="filterThreshold.value"
                                        />
                                    </template>
                                </div>
                            </div>
                        </template>
                        <template v-if="filterType === 'lowerThan' || filterType === 'greaterThan'">
                            <div class="columns is-variable">
                                <div class="column is-6">
                                    <template v-if="filterDataType === 'number'">
                                        <form-number
                                            :disabled="disabled"
                                            :name="'form-selectfilters-number'"
                                            :label="'Value'"
                                            v-model="filterThreshold.value"
                                        ></form-number>
                                    </template>
                                    <template v-if="filterDataType === 'date'">
                                        <form-date
                                            :label="'Date to'"
                                            :name="'form-selectfilters-date'"
                                            v-model="filterThreshold.value"
                                        />
                                    </template>
                                </div>
                            </div>
                        </template>
                        <template v-if="filterType === 'between'">
                            <div class="columns is-variable">
                                <div class="column">
                                    <template v-if="filterDataType === 'number'">
                                        <form-number
                                            :disabled="disabled"
                                            :name="'form-selectfilters-number-min-number'"
                                            :label="'Value min'"
                                            v-model="filterThreshold.from"
                                        ></form-number>
                                    </template>
                                    <template v-else-if="filterDataType === 'date'">
                                        <form-date
                                            :label="'Date to'"
                                            :name="'form-selectfilters-number-min-date'"
                                            :v-validate="`date_format:yyyy-MM-dd|${filterThreshold.from !== filterThreshold.to ? `before:${filterThreshold.to}` :  ``}`"
                                            v-model="filterThreshold.from"
                                        />
                                    </template>
                                </div>
                                <div class="column">
                                    <template v-if="filterDataType === 'number'">
                                        <form-number
                                            :disabled="disabled"
                                            :name="'form-selectfilters-number-max-number'"
                                            :label="'Value max'"
                                            v-model="filterThreshold.to"
                                        ></form-number>
                                    </template>
                                    <template v-else-if="filterDataType === 'date'">
                                        <form-date
                                            :label="'Date from'"
                                            :name="'form-selectfilters-number-max-date'"
                                            :v-validate="`date_format:yyyy-MM-dd|${filterThreshold.to !== filterThreshold.from ? `after:${filterThreshold.from}` :  ``}`"
                                            v-model="filterThreshold.to"
                                        />
                                    </template>
                                </div>
                            </div>
                        </template>
                    </template>
                </template>
            </div>
        </div>
        <span
            class="form__error form__selectfilters_error"
            v-if="errors.first(toKebabCase(name))"
        >{{ vValidateCustomError || errors.first(toKebabCase(name)) }}</span>
        <template v-if="$slots.default && !hintPositionTop">
            <div class="form__selectfilters_hint">
                <slot></slot>
            </div>
        </template>
        <div class="form__selectfilters_actions">
            <component-button
                :disabled="!allowAddButton"
                :name="'form-selectfilters-actions-add'"
                :theme="'default'"
                :icon="true"
                :icon-name="'plus-circle'"
                :icon-align="'left'"
                :text="true"
                :size="'tiny'"
                @click="emitAdd"
            >Add filter
            </component-button>
        </div>
        <div class="form__selectfilters_selected">
            <ul class="form__selectfilters_selected_list">
                <li
                    class="form__selectfilters_selected_list_item"
                    v-for="(item, index) in valueOutput"
                    :key="index"
                >
                    <div class="columns is-variable">
                        <div class="column is-narrow">
                            <div class="form__selectfilters_selected_list_item_remove">
                                <component-button
                                    :theme="''"
                                    :icon="true"
                                    :icon-raw="true"
                                    :icon-name="'x'"
                                    :name="'form-selectfilters-selected-list-item-remove'"
                                    @click="emitRemove(index)"
                                ></component-button>
                            </div>
                        </div>
                        <div class="column is-narrow">
                            <div class="form__selectfilters_selected_list_item_column">{{ items.length ? items.find(obj => obj.value === item.column).label : '' }}</div>
                        </div>
                        <div class="column is-narrow">
                            <div class="form__selectfilters_selected_list_item_type">
                                <template v-if="item.type === 'like'">Contains</template>
                                <template v-if="item.type === 'equals'">Equals</template>
                                <template v-else-if="item.type === 'greaterThan'">Greater than</template>
                                <template v-else-if="item.type === 'lowerThan'">Lower than</template>
                            </div>
                        </div>
                        <div class="column is-narrow">
                            <div class="form__selectfilters_selected_list_item_value">
                                <template v-if="item.type === 'equals' || item.type === 'like' || item.type === 'lowerThan' || item.type === 'greaterThan' || item.type === 'in'">{{ item.threshold.name || (item.threshold.value && item.threshold.value.constructor === Array ? item.threshold.value.join(',\n') : item.threshold.value) }}</template>
                                <template v-else-if="item.type === 'between'">{{ item.threshold.from }} - {{ item.threshold.to }}</template>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>

import FormSelect from '@/components/default/forms/FormSelect'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormText from '@/components/default/forms/FormText'
import ComponentButton from '@/components/default/shared/ComponentButton'
import FormNumber from '@/components/default/forms/FormNumber'
import FormDate from '@/components/default/forms/FormDate'
import FormSelectOrgUnits from '@/components/default/forms/FormSelectOrgUnits'
import { User } from '@/User'

export default {
  name: 'form-select-filters',
  components: { FormSelectOrgUnits, FormDate, FormNumber, ComponentButton, FormText, FormRadioHorizontal, FormSelect },
  data () {
    return {
      filterColumn: '',
      filterType: '',
      filterThreshold: {
        value: '',
        name: '',
        from: '',
        to: ''
      },
      user: new User()
    }
  },
  props: {
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true,
      default: 'Label'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    //
    dataVvAs: {
      type: String,
      default: ''
    },
    vValidateCustomError: {
      type: String
    },
    vValidate: {
      default: ''
    },
    hintPositionTop: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    valueOutput () {
      const values = this.value
      const items = this.items
      let valueFiltered = []

      for (let value of values) {
        if (items.find(obj => obj.value === value.column)) {
          valueFiltered.push(value)
        }
      }

      return valueFiltered
    },
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    allowAddButton () {
      return (
        (
          this.filterColumn &&
      (
        this.filterColumn.includes('OrganisationUnit') &&
      this.filterThreshold.value
      )
        ) ||
      (
        this.filterType && (
          (
            this.filterType === 'between' && this.filterThreshold.from && this.filterThreshold.to
          ) ||
      (
        (
          this.filterType === 'equals' ||
      this.filterType === 'like' ||
      this.filterType === 'lowerThan' ||
      this.filterType === 'greaterThan'
        ) && this.filterThreshold.value
      )
        )
      )
      )
    },
    filterTypeList () {
      const itemsList = this.items
      const filterType = this.filterDataType
      const selectedItem = itemsList.find(obj => obj.value === this.filterColumn)
      const selectedItemFilters = selectedItem ? selectedItem.filters : []

      return selectedItemFilters.map(obj => {
        let nameValue = obj

        if (obj === 'greaterThan') {
          nameValue = filterType === 'number' ? 'Greater than' : 'After'
        }
        if (obj === 'lowerThan') {
          nameValue = filterType === 'number' ? 'Lower than' : 'Before'
        }

        return {
          name: nameValue,
          value: obj
        }
      })
    },
    filterDataType () {
      const itemsList = this.items
      const selectedItem = itemsList.find(obj => obj.value === this.filterColumn)

      return selectedItem ? selectedItem.dataType : undefined
    },
    filterAvailableValues () {
      const itemsList = this.items
      const selectedItem = itemsList.find(obj => obj.value === this.filterColumn)

      return selectedItem ? selectedItem.availableValues : []
    },
    filterAvailableValuesMapped () {
      const values = this.filterAvailableValues
      const entries = Object.entries(values)
      let output = entries.map(entry => {
        return {
          value: entry[0],
          label: entry[1]
        }
      })

      return output
    }
  },
  methods: {
    setFilterValue (data) {
      const dataValue = data.id || data.value
      const dataLabel = data.label || data.name

      this.filterThreshold.value = dataValue
      this.filterThreshold.name = dataLabel || undefined
    },
    setFilterColumn (data) {
      this.filterColumn = data.value
      if (data && (data.value.includes('OrganisationUnit') || data.availableValues.constructor === Object)) {
        this.filterType = 'equals'
      }
    },
    emitAdd () {
      const thresholdBase = this.filterThreshold
      const column = this.filterColumn
      const type = this.filterType
      const performEmit = () => {
        if (thresholdBase.value) {
          threshold.value = thresholdBase.value
        }
        if (thresholdBase.name) {
          threshold.name = thresholdBase.name
        }
        if (thresholdBase.from) {
          threshold.from = thresholdBase.from
        }
        if (thresholdBase.to) {
          threshold.to = thresholdBase.to
        }

        this.$emit('emit-add', { column, type, threshold })

        this.filterColumn = ''
        this.filterType = ''
        this.filterThreshold = {
          value: '',
          name: '',
          from: '',
          to: ''
        }
      }
      let threshold = {}

      if (this.filterType === 'between' && this.filterDataType === 'date') {
        this.$validator.validate('form-selectfilters-number-min-date').then(result => {
          if (result) {
            this.$validator.validate('form-selectfilters-number-min-date').then(result => {
              performEmit()
            })
          }
        })
      } else {
        performEmit()
      }
    },
    emitRemove (data) {
      this.$emit('emit-remove', data)
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/forms/formselectfilters";
</style>
